import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import AppBar from './AppBar';
import FilterBar from './FilterBar';
import KpiCard from './KpiCard';
import DrillModal from './DrillModal';
import SalesDashboard from './SalesDashboard';
import { Filters, EMPTY_FILTERS } from './apiClient';
import { theme, fmtNum } from './theme';
import { useWarehouseRows, WarehouseRow, WarehouseDataset } from './useWarehouseRows';
import { useAliases } from './useAliases';

type Breakdown = {
  bin: number;       // Rack
  virtual: number;
  floor: number;
  manual: number;
  tempBlocked: number;
  areaBlocked: number;
};

type Kpis = {
  totalLocations: number;
  totalCapacity: number;
  usedCapacity: number;
  freeCapacity: number;
  sellableCapacity: number;
  blockedLocations: number;
  blockedCapacity: number;
  futureLocations: number;
  committedCapacity: number;      // min-commitment pallets (total obligation)
  shortfallCapacity: number;      // gap: max(0, minPallets - actual) — unfilled commitment
  breakdown: Breakdown;
};

const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/* ----------------------------------------------------------------------------
 * Apply the multi-select filters to the raw warehouse rows.
 * Empty array in any filter dimension means "All" for that dimension.
 *  - warehouses : keep rows where WAREHOUSE is in the picked list
 *  - chambers   : keep rows where STORAGE_AREA is in the picked list
 *  - palletTypes: backend doesn't expose pallet type per row yet, so this
 *                 dimension currently has no row-level effect; the filter is
 *                 still wired and shown so the manager can select it and we
 *                 can apply it the moment the backend supports it.
 * -------------------------------------------------------------------------- */
// Dummy/test warehouses to always exclude from the dashboard
const EXCLUDED_WAREHOUSES = new Set(['LFS']);

// Chamber filter values are composite "WAREHOUSE-STORAGE_AREA" keys (e.g. "DPW-7K")
// so filtering by area is always scoped to a specific warehouse.
function applyFilters(rows: WarehouseRow[], f: Filters): WarehouseRow[] {
  let out = rows.filter(r => !EXCLUDED_WAREHOUSES.has(r.WAREHOUSE));
  if (f.warehouses.length)  out = out.filter(r => f.warehouses.includes(r.WAREHOUSE));
  if (f.chambers.length)    out = out.filter(r => f.chambers.includes(`${r.WAREHOUSE}-${r.STORAGE_AREA || 'OTHER'}`));
  if (f.palletTypes.length) out = out.filter(r => f.palletTypes.includes(r.SHELF_SIZE));
  return out;
}

// Generic warehouse/area/pallet-type filter for raw arrays from other endpoints.
// When an area or pallet-type filter is active, rows that lack the corresponding
// field are EXCLUDED (not passed through) — otherwise unattributable data
// inflates Occupied and zeroes out Sellable.
function filterRaw<T extends Record<string, any>>(arr: T[], f: Filters): T[] {
  let out = arr.filter(r => !EXCLUDED_WAREHOUSES.has(String(r.WAREHOUSE ?? '').trim()));
  if (f.warehouses.length)  out = out.filter(r => f.warehouses.includes(String(r.WAREHOUSE ?? '').trim()));
  if (f.chambers.length)    out = out.filter(r => {
    const wh = String(r.WAREHOUSE ?? '').trim();
    if (!wh) return false;
    // Try STORAGE_AREA first, then STORAGE_LOCATION (customer-storage uses STORAGE_LOCATION)
    // Normalize empty area to 'OTHER' to match useWarehouseRows + buildWhStats
    const area = String(r.STORAGE_AREA ?? '').trim()
              || String(r.STORAGE_LOCATION ?? '').trim()
              || 'OTHER';
    // Match composite key directly, or try "WH-AREA"
    if (f.chambers.includes(`${wh}-${area}`)) return true;
    if (f.chambers.includes(area)) return true;
    return false;
  });
  if (f.palletTypes.length) out = out.filter(r => {
    const pt = String(r.SHELF_SIZE ?? r.PALLET_TYPE ?? '').trim();
    if (!pt) return false; // no pallet type → exclude when filter active
    return f.palletTypes.includes(pt);
  });
  return out;
}

/* ────────────────────────────────────────────────────────────────────────
 * Per-warehouse capacity engine — MIRRORS legacy
 *   ExecutiveDashboard.tsx > dynamicCapacityStats (lines 895-1050).
 * This is the AUTHORITATIVE formula:
 *   • Rack cap = raw LKASEG, used = LKASEG − LKFSEG (no level adjustments)
 *   • Floor pallets are RESIDUAL: max(0, ibmPallets - rackUsed - blockedCap)
 *   • Manual cap = MAX per (warehouse, storage_location) of TOTAL_CAPACITY
 *   • tCap   = rackCap + manualCap                   (blocked & virtual NOT in cap)
 *   • tUsed  = rackUsed + blockedCap + virtualUsed + manualPallets + floorPallets
 *   • tFree  = max(0, tCap - rackUsed - blockedCap - manualPallets)
 * This single function powers BOTH the KPI cards and the per-warehouse drill.
 * ────────────────────────────────────────────────────────────────────────*/
/* ─── Customer commitment enrichment ────────────────────────────────────
 * The CustomerContracts table stores commitments in many units. Convert each
 * to PALLETS using the same factors as the legacy ExecutiveDashboard, then
 * compute SHORTFALL = max(0, minPallets − actualCustomerPallets). Only the
 * shortfall counts as reserved/committed capacity (the actual occupancy is
 * already in rack/manual). */
const COMMIT_FACTORS: Record<string, { factor: number; unit: string }> = {
  PP:   { factor: 1,        unit: 'PP'   },
  SQM:  { factor: 0.8333,   unit: 'SQM'  }, // 1 pallet = 1.2 SQM
  SQFT: { factor: 0.07742,  unit: 'SQFT' }, // 1 pallet = 12.917 SQFT
  BINS: { factor: 1,        unit: 'Bins' },
  MT:   { factor: 0.714,    unit: 'MT'   },
  KT:   { factor: 714.29,   unit: 'KT'   },
  CBM:  { factor: 0.5,      unit: 'CBM'  },
};

export type EnrichedContract = {
  id?: number;
  customer: string;
  warehouse: string;       // 'All' or comma-separated
  warehouses: string[];    // exploded list
  type: string;            // PP/SQM/...
  unit: string;
  value: number;           // raw commitment value
  factor: number;
  minPallets: number;
  actual: number;
  shortfall: number;
  isMet: boolean;
  storageType: string;
  notes: string;
};

function enrichContracts(contracts: any[], customerStorage: any[]): EnrichedContract[] {
  // 1. Build per-customer + per-(customer, warehouse) actual pallet map.
  const actualMap: Record<string, number> = {};
  (customerStorage || []).forEach((it: any) => {
    const cust = String(it.CUSTOMER || it.customer || '').trim();
    const wh   = String(it.WAREHOUSE || it.warehouse || '').trim();
    const used = num(it.USED_PALLETS ?? it.used_pallets);
    if (!cust) return;
    actualMap[cust] = (actualMap[cust] || 0) + used;
    if (wh) actualMap[`${cust}__${wh}`] = (actualMap[`${cust}__${wh}`] || 0) + used;
  });

  return (contracts || []).map((c: any) => {
    const customer = String(c.customer_name ?? c.customer ?? '').trim();
    const warehouse = String(c.facility ?? c.warehouse ?? c.WAREHOUSE ?? 'All').trim() || 'All';
    const warehouses = warehouse === 'All' ? ['All'] : warehouse.split(',').map(s => s.trim()).filter(Boolean);
    const type = String(c.storage_type ?? c.commitment_type ?? c.type ?? 'PP').toUpperCase();
    const meta = COMMIT_FACTORS[type] || COMMIT_FACTORS.PP;
    const value = num(c.minimum_value ?? c.commitment_value ?? c.commitment ?? 0);
    const minPallets = Math.ceil(value * meta.factor);

    // Customer field can be a comma-separated list (group commitment).
    // Sum actuals across every (customer × warehouse) combination it covers.
    const customers = customer.split(',').map(s => s.trim()).filter(Boolean);
    let actual = 0;
    customers.forEach(cust => {
      if (warehouses.includes('All')) {
        actual += actualMap[cust] || 0;
      } else {
        warehouses.forEach(wh => { actual += actualMap[`${cust}__${wh}`] || 0; });
      }
    });
    const shortfall = Math.max(0, minPallets - actual);
    return {
      id: c.id,
      customer, warehouse, warehouses,
      type, unit: meta.unit, value, factor: meta.factor,
      minPallets, actual, shortfall,
      isMet: shortfall === 0,
      storageType: String(c.storage_type ?? type),
      notes: String(c.notes ?? ''),
    };
  });
}

// Capacity: seg 1-2 → as-is, seg 3-9 + LKVER=10 → 2, seg 3-9 + LKVER≠10 → 1, seg ≥10 → as-is.
// Used: always raw LKASEG − LKFSEG.
const calcCap = (seg: number, level: number): number => {
  if (seg <= 0) return 0;
  if (seg <= 2) return seg;
  if (seg < 10) return level === 10 ? 2 : 1;
  return seg;
};

export type WhStats = {
  warehouse: string;
  rackCap: number; rackUsed: number;
  blockedBins: number; blockedCap: number;
  areaBlockedBins: number; areaBlockedCap: number;
  virtualUsed: number;
  manualCap: number; manualPallets: number;
  floorPallets: number;
  // Per-area breakdown (for the drill modal)
  rackByArea: Record<string, { cap: number; used: number; binCount: number }>;
  manualByLocation: Record<string, { cap: number; used: number }>;
  // Totals
  tCap: number; tUsed: number; tFree: number;
};

function buildWhStats(
  rawAll: any[], virtual: any[], customer: any[], areaBlocked: any[],
  weeklyEntries: any[] = [],
): Record<string, WhStats> {
  const whMap: Record<string, WhStats> = {};
  const ensure = (wh: string): WhStats => {
    if (!whMap[wh]) whMap[wh] = {
      warehouse: wh,
      rackCap: 0, rackUsed: 0,
      blockedBins: 0, blockedCap: 0,
      areaBlockedBins: 0, areaBlockedCap: 0,
      virtualUsed: 0,
      manualCap: 0, manualPallets: 0, floorPallets: 0,
      rackByArea: {}, manualByLocation: {},
      tCap: 0, tUsed: 0, tFree: 0,
    };
    return whMap[wh];
  };

  // 1. Physical bins → rack / blocked (LPFSTA='2')  (level-aware)
  // NOTE: blockedCap includes ALL LPFSTA='2' bins — both temp-blocked AND
  // area-blocked (LGPGM039). The /api/warehouse query doesn't return
  // LPDUPGM so we can't distinguish them here. The area-blocked count
  // comes from the separate /area-customer-blocked API and is subtracted
  // from blockedCap later to get the exclusive temp-blocked count.
  rawAll.forEach((item: any) => {
    if (item?.SOURCE === 'MANUAL') return;
    const wh = String(item.WAREHOUSE || '').trim();
    if (!wh) return;
    const seg = num(item.TOTAL_NO_OF_SEGMENTS);
    const free = num(item.FREE_SEGMENTS);
    const lvl = num(item.NUMBER_OF_LEVELS);
    if (seg >= 99) return; // virtual handled below
    const blocked = String(item.LPFSTA || '').trim() === '2' || item.OCCUPANCY_STATUS === 'TEMP_BLOCKED';

    const row = ensure(wh);
    const area = String(item.STORAGE_AREA || '').trim() || 'OTHER';
    // Use FULL STORAGE_AREA as rackByArea key (e.g. "DPW-7K") so it matches
    // the FilterBar selection and drill labels. Do NOT strip to prefix.
    const areaKey = area;

    if (blocked) {
      const cap = calcCap(seg, lvl);
      row.blockedBins += 1;
      row.blockedCap += cap;
      // Do NOT add to rackCap or binCount — blocked bins excluded from total capacity & locations
      (row.rackByArea[areaKey] ||= { cap: 0, used: 0, binCount: 0 });
    } else if (seg === 0) {
      // Floor bin (no capacity); floor pallets computed as residual later
    } else {
      const cap = calcCap(seg, lvl);
      const used = Math.max(0, seg - free);  // raw LKASEG - LKFSEG
      row.rackCap += cap;
      row.rackUsed += used;
      const a = (row.rackByArea[areaKey] ||= { cap: 0, used: 0, binCount: 0 });
      a.cap += cap;
      a.used += used;
      a.binCount += 1;
    }
  });

  // 2. Virtual used (not part of cap)
  virtual.forEach((item: any) => {
    const wh = String(item.WAREHOUSE || '').trim();
    if (!wh) return;
    const seg = num(item.TOTAL_NO_OF_SEGMENTS ?? item.TOTAL_SEGMENTS);
    const free = num(item.FREE_SEGMENTS);
    ensure(wh).virtualUsed += Math.max(0, seg - free);
  });

  // 3. Manual PALLETS (used) from customer-storage-merged STORAGE_TYPE='MANUAL'
  customer.forEach((item: any) => {
    if (String(item.STORAGE_TYPE || '').toUpperCase() !== 'MANUAL') return;
    const wh = String(item.WAREHOUSE || '').trim();
    if (!wh) return;
    const loc = String(item.STORAGE_LOCATION || '').trim() || 'MANUAL';
    const row = ensure(wh);
    row.manualPallets += num(item.USED_PALLETS);
    const m = (row.manualByLocation[loc] ||= { cap: 0, used: 0 });
    m.used += num(item.USED_PALLETS);
  });

  // 3b. Manual CAPACITY from /weekly-entries — mirrors legacy Utilization
  //     dashboard (lines 2083-2107). Uses `area_capacity` field (NOT `capacity`,
  //     which is per-client) keyed by `warehouse_area`. Takes the LATEST entry
  //     per (warehouse, warehouse_area) where area_capacity > 0, then sums ONE
  //     capacity per area (not per client).
  const sortedWeekly = [...weeklyEntries].sort((a: any, b: any) =>
    (num(b.year) - num(a.year)) || (num(b.week_number) - num(a.week_number)),
  );
  const latestAreaCap: Record<string, { wh: string; area: string; cap: number }> = {};
  sortedWeekly.forEach((e: any) => {
    const areaCap = num(e.area_capacity);
    if (areaCap <= 0) return;
    const wh = String(e.warehouse || '').trim();
    if (!wh) return;
    const area = String(e.warehouse_area || 'default').trim();
    const key = `${wh}__${area}`;
    if (!latestAreaCap[key]) latestAreaCap[key] = { wh, area, cap: areaCap };
  });
  Object.values(latestAreaCap).forEach(({ wh, area, cap }) => {
    const row = ensure(wh);
    row.manualCap += cap;
    const m = (row.manualByLocation[area] ||= { cap: 0, used: 0 });
    m.cap = cap;
  });

  // 3c. Fallback for warehouses (DPW, JED, ...) where weekly entries have no
  //     `area_capacity` but DO have a per-client `capacity` column. Take the
  //     latest entry per (warehouse, client_name) and SUM `capacity` across
  //     clients — only for warehouses still missing a manual capacity.
  const whMissingCap = new Set<string>();
  customer.forEach((item: any) => {
    if (String(item.STORAGE_TYPE || '').toUpperCase() !== 'MANUAL') return;
    const wh = String(item.WAREHOUSE || '').trim();
    if (wh) whMissingCap.add(wh);
  });
  Object.values(whMap).forEach(r => { if (r.manualCap > 0) whMissingCap.delete(r.warehouse); });

  if (whMissingCap.size > 0) {
    const latestByWhClient: Record<string, any> = {};
    sortedWeekly.forEach((e: any) => {
      const wh = String(e.warehouse || '').trim();
      if (!whMissingCap.has(wh)) return;
      const cli = String(e.client_name || '').trim();
      const key = `${wh}__${cli}`;
      if (!latestByWhClient[key]) latestByWhClient[key] = e;
    });
    Object.values(latestByWhClient).forEach((e: any) => {
      const wh = String(e.warehouse || '').trim();
      const cap = num(e.capacity);
      if (!wh || cap <= 0) return;
      const area = String(e.warehouse_area || wh).trim();
      const row = ensure(wh);
      row.manualCap += cap;
      const m = (row.manualByLocation[area] ||= { cap: 0, used: 0 });
      m.cap += cap;
    });
  }

  // 4. Floor = residual: max(0, ibmPallets - rackUsed - blockedCap)
  //    ibmPallets = sum of non-MANUAL customer USED_PALLETS per warehouse
  const ibmByWh: Record<string, number> = {};
  customer.forEach((item: any) => {
    if (String(item.STORAGE_TYPE || '').toUpperCase() === 'MANUAL') return;
    const wh = String(item.WAREHOUSE || '').trim();
    if (!wh) return;
    ibmByWh[wh] = (ibmByWh[wh] || 0) + num(item.USED_PALLETS);
  });
  Object.values(whMap).forEach(row => {
    const ibm = ibmByWh[row.warehouse] || 0;
    row.floorPallets = Math.max(0, ibm - row.rackUsed - row.blockedCap);
    // rackCap already includes blocked bins' capacity — don't add blockedCap again
    row.tCap  = row.rackCap + row.manualCap;
    row.tUsed = row.rackUsed + row.blockedCap + row.virtualUsed + row.manualPallets + row.floorPallets;
    row.tFree = Math.max(0, row.tCap - row.rackUsed - row.blockedCap - row.manualPallets);
  });

  // 5. Make sure manual-only warehouses (no physical bins) appear too
  customer.forEach((item: any) => {
    const wh = String(item.WAREHOUSE || '').trim();
    if (wh) ensure(wh);
  });
  areaBlocked.forEach((item: any) => {
    const wh = String(item.WAREHOUSE || '').trim();
    if (wh) ensure(wh);
  });

  return whMap;
}

/* Aggregate using the legacy dynamicCapacityStats formula. */
function aggregate(
  filteredRows: WarehouseRow[],
  rawAllFiltered: any[],
  virtualFiltered: any[],
  customerFiltered: any[],
  areaBlockedFiltered: any[],
  blockedExtra: any,
  contracts: any[],
  whStats: Record<string, WhStats>,
  customerForCommit: any[],
  hasSubFilters: boolean = false,
  hasPalletFilter: boolean = false,
  whStatsBase: Record<string, WhStats> = {},
): Kpis {
  // Sum across all warehouses in the breakdown
  let rackCap = 0, rackUsed = 0, blockedCap = 0, virtualUsed = 0;
  let manualCap = 0, manualPallets = 0, floorPallets = 0;
  Object.values(whStats).forEach(w => {
    rackCap += w.rackCap; rackUsed += w.rackUsed;
    blockedCap += w.blockedCap;
    virtualUsed += w.virtualUsed;
    manualCap += w.manualCap; manualPallets += w.manualPallets;
    floorPallets += w.floorPallets;
  });

  // ---- area customer blocked (from the dedicated /area-customer-blocked API) ----
  const areaBlockedPalletsFull = areaBlockedFiltered.reduce(
    (s, b) => s + num(b.PALLET_COUNT ?? b.BLOCKED_PALLETS ?? b.TOTAL_NO_OF_SEGMENTS ?? 1),
    0,
  );

  // ---- legacy dynamicCapacityStats totals ----
  const enrichedContracts = enrichContracts(contracts, customerForCommit);
  const committedCapacity = enrichedContracts.reduce((s, c) => s + c.minPallets, 0);
  const shortfallFull = enrichedContracts.reduce((s, c) => s + c.shortfall, 0);

  // When pallet filter is active: non-pallet items distributed proportionally
  // using totalRackCap(filtered) / totalRackCap(base).
  // When only area filter: areaBlocked included as-is (has area dim), commitment excluded.
  // When no sub-filters: everything included as-is.
  let areaBlockedPallets: number;
  let shortfallCapacity: number;
  let adjVirtualUsed = virtualUsed;
  let adjManualPallets = manualPallets;
  let adjFloorPallets = floorPallets;
  let adjBlockedCap = blockedCap;
  let adjManualCap = manualCap;

  if (hasPalletFilter) {
    // Per-warehouse proportional distribution — matches warehouseData exactly.
    // Items WITH pallet dim (SHELF_SIZE): use actual filtered values (rackCap, rackUsed, blockedCap).
    // Items WITHOUT pallet dim: distribute per-warehouse using ratio = wh.rackCap / base.rackCap.
    // Using per-warehouse ratios (not a single overall ratio) because different warehouses
    // have different pallet type distributions.
    let sumVirtual = 0, sumManPal = 0, sumFloor = 0, sumManCap = 0;
    let sumAreaB = 0, sumCommit = 0;

    // Build per-wh commitment shortfall map (same logic as commitShortfallByWh)
    const allWh = Object.keys(whStatsBase);
    const shortfallByWh: Record<string, number> = {};
    enrichedContracts.forEach(c => {
      if (c.shortfall <= 0) return;
      let targets: string[];
      if (c.warehouses.includes('All')) {
        targets = allWh;
      } else {
        targets = c.warehouses.filter((cw: string) => allWh.includes(cw));
        if (targets.length === 0) targets = allWh;
      }
      if (targets.length === 0) return;
      const per = Math.ceil(c.shortfall / targets.length);
      let remaining = c.shortfall;
      targets.forEach(wh => {
        const share = Math.min(per, remaining);
        shortfallByWh[wh] = (shortfallByWh[wh] || 0) + share;
        remaining -= share;
      });
    });

    Object.values(whStats).forEach(w => {
      const wh = w.warehouse;
      const base = whStatsBase[wh];
      const baseRackCap = base ? base.rackCap : 0;
      const ratio = baseRackCap > 0 ? w.rackCap / baseRackCap : 0;

      sumVirtual += Math.round((base ? base.virtualUsed : 0) * ratio);
      sumManPal  += Math.round((base ? base.manualPallets : 0) * ratio);
      sumFloor   += Math.round((base ? base.floorPallets : 0) * ratio);
      sumManCap  += Math.round((base ? base.manualCap : 0) * ratio);

      const whAreaB = areaBlockedFiltered
        .filter(b => b.WAREHOUSE === wh)
        .reduce((s, b) => s + num(b.PALLET_COUNT ?? b.BLOCKED_PALLETS ?? b.TOTAL_NO_OF_SEGMENTS ?? 1), 0);
      sumAreaB  += Math.round(whAreaB * ratio);
      sumCommit += Math.round((shortfallByWh[wh] || 0) * ratio);
    });

    adjVirtualUsed   = sumVirtual;
    adjManualPallets = sumManPal;
    adjFloorPallets  = sumFloor;
    adjBlockedCap    = blockedCap;  // blocked bins HAVE SHELF_SIZE → actual filtered value
    adjManualCap     = sumManCap;
    areaBlockedPallets = sumAreaB;
    shortfallCapacity  = sumCommit;
  } else if (hasSubFilters) {
    // Area filter only: areaBlocked already filtered by area, commitment excluded
    areaBlockedPallets = areaBlockedPalletsFull;
    shortfallCapacity = 0;
  } else {
    // No sub-filters: everything as-is
    areaBlockedPallets = areaBlockedPalletsFull;
    shortfallCapacity = shortfallFull;
  }

  // totalCapacity: rack + manual (area-blocked bins are already in rackCap
  // or blockedCap from the main /api/warehouse query — adding areaBlocked
  // again would double-count them)
  const totalCapacity = rackCap + adjManualCap;
  // usedCapacity: rack-used + temp-blocked + area-blocked + virtual + manual + floor + shortfall
  const usedCapacity  = rackUsed + adjBlockedCap + areaBlockedPallets + adjVirtualUsed + adjManualPallets + adjFloorPallets + shortfallCapacity;
  const sellableCapacity = Math.max(0, totalCapacity - usedCapacity);
  const freeCapacity  = sellableCapacity;

  // Display split: tempBlocked = LPFSTA='2' only, areaBlocked = LGPGM039 only (no overlap)
  const exclusiveTempBlocked = adjBlockedCap;

  const breakdown: Breakdown = {
    bin:         rackUsed,
    virtual:     adjVirtualUsed,
    floor:       adjFloorPallets,
    manual:      adjManualPallets,
    tempBlocked: exclusiveTempBlocked,
    areaBlocked: areaBlockedPallets,
  };

  // "Locations" in this dashboard = number of distinct warehouses
  const whSet = new Set<string>();
  filteredRows.forEach(r => { if (r.WAREHOUSE) whSet.add(r.WAREHOUSE); });
  const totalLocations = whSet.size;

  const blockedWhSet = new Set<string>();
  filteredRows.forEach(r => { if (r.BLOCKED && r.WAREHOUSE) blockedWhSet.add(r.WAREHOUSE); });
  const tempBlockedLocations = blockedWhSet.size;
  const tempBlockedCapacity = exclusiveTempBlocked;

  // Area Customer Blocked (from /warehouse/area-customer-blocked/stats).
  const areaBlockedLocations = num(
    blockedExtra?.TOTAL_BLOCKED_BINS ?? blockedExtra?.totalBlockedBins ?? 0,
  );
  const areaBlockedCapacity = num(
    blockedExtra?.TOTAL_BLOCKED_PALLETS ?? blockedExtra?.totalBlockedPallets ?? 0,
  );

  const blockedLocations = tempBlockedLocations + areaBlockedLocations;
  const blockedCapacity  = exclusiveTempBlocked + Math.max(areaBlockedCapacity, areaBlockedPallets);

  // "Future Locations" in the mockup = BIN_BLOCKED_COUNT (LPSPKZ='X') from
  // the same area-customer-blocked/stats endpoint — bins reserved for future
  // use that aren't yet temp-blocked.
  const futureLocations = num(
    blockedExtra?.BIN_BLOCKED_COUNT ?? blockedExtra?.binBlockedCount ?? 0,
  );

  return {
    totalLocations, totalCapacity, usedCapacity, freeCapacity, sellableCapacity,
    blockedLocations, blockedCapacity, futureLocations,
    committedCapacity, shortfallCapacity,
    breakdown,
  };
}

type DashTab = 'production' | 'sales';

const OperationsDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<DashTab>('production');
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const {
    rows, rawAll, virtual, customerStorage, areaBlocked, areaBlockedStats, contracts, weeklyEntries,
    loading, error,
    paused, setPaused, lastRefresh, refresh,
  } = useWarehouseRows();

  const [drill, setDrill] = useState<{ key: string; title: string; accent: string } | null>(null);
  const { resolve: aliasResolve } = useAliases();

  // Recompute KPIs whenever data or filters change (cheap, all client-side)
  const filteredRows = useMemo(() => applyFilters(rows, filters), [rows, filters]);
  const rawAllFiltered = useMemo(() => filterRaw(rawAll, filters), [rawAll, filters]);
  const virtualFiltered = useMemo(() => filterRaw(virtual, filters), [virtual, filters]);
  const customerFiltered = useMemo(() => filterRaw(customerStorage, filters), [customerStorage, filters]);

  // Derive the set of warehouses implied by the filter state. When only
  // chambers are selected (no explicit warehouse), we extract the warehouse
  // prefixes from the composite keys (e.g. "DPW-12K" → "DPW"). This ensures
  // downstream data that cannot filter by area (weekly entries, commitments,
  // customer-storage-for-commit) is still scoped to the right warehouses.
  const impliedWarehouses = useMemo(() => {
    if (filters.warehouses.length) return new Set(filters.warehouses);
    if (filters.chambers.length) {
      const whs = new Set<string>();
      filters.chambers.forEach(ck => {
        const idx = ck.indexOf('-');
        if (idx > 0) whs.add(ck.substring(0, idx));
      });
      return whs;
    }
    return null; // null = no warehouse restriction
  }, [filters.warehouses, filters.chambers]);

  // Commitments live at the warehouse level only (no area / pallet-type dim),
  // so the customer-storage used to compute their "actual" must only be
  // scoped by WAREHOUSE — otherwise area/pallet-type filters zero out the
  // actuals and inflate shortfall.
  const customerFilteredWhOnly = useMemo(
    () => (impliedWarehouses
      ? (customerStorage || []).filter((r: any) =>
          impliedWarehouses.has(String(r.WAREHOUSE ?? '').trim()))
      : customerStorage),
    [customerStorage, impliedWarehouses],
  );
  // Area-blocked data has WAREHOUSE + STORAGE_LOCATION (LPORT-LPBER).
  // It does NOT have STORAGE_AREA or SHELF_SIZE, so palletType filters
  // can't apply. But chamber filters CAN match: STORAGE_LOCATION is the
  // same format as chambers ("WH-AREA" composite keys).
  const areaBlockedFiltered = useMemo(
    () => {
      let out = (areaBlocked || []).filter((r: any) =>
        !EXCLUDED_WAREHOUSES.has(String(r.WAREHOUSE ?? '').trim()));
      if (filters.warehouses.length)
        out = out.filter((r: any) => filters.warehouses.includes(String(r.WAREHOUSE ?? '').trim()));
      if (impliedWarehouses)
        out = out.filter((r: any) => impliedWarehouses.has(String(r.WAREHOUSE ?? '').trim()));
      // Apply chamber filter using STORAGE_LOCATION (LPORT-LPBER).
      // Chamber keys are "WH-AREA", and STORAGE_LOCATION from the area-blocked
      // API is also "WH-AREA" (LPORT-LPBER), so direct match works.
      if (filters.chambers.length) {
        const chamberSet = new Set(filters.chambers);
        out = out.filter((r: any) => {
          const loc = String(r.STORAGE_LOCATION ?? '').trim();
          if (chamberSet.has(loc)) return true;
          // Also try "WAREHOUSE-AREA" composite in case STORAGE_LOCATION is just the area part
          const wh = String(r.WAREHOUSE ?? '').trim();
          return chamberSet.has(`${wh}-${loc}`);
        });
      }
      return out;
    },
    [areaBlocked, filters.warehouses, filters.chambers, impliedWarehouses],
  );

  // Weekly entries scoped by warehouse AND area when chamber filter is active.
  // `warehouse_area` in weekly entries is the area key (e.g. "7K-M", "CLD-01").
  // Chamber filter values are "WH-AREA" composites. Extract the area parts
  // so we can match against warehouse_area.
  const impliedAreas = useMemo(() => {
    if (!filters.chambers.length) return null; // null = no area restriction
    const areas = new Map<string, Set<string>>(); // wh → Set<area>
    filters.chambers.forEach(ck => {
      const idx = ck.indexOf('-');
      if (idx > 0) {
        const wh = ck.substring(0, idx);
        const area = ck.substring(idx + 1);
        if (!areas.has(wh)) areas.set(wh, new Set());
        areas.get(wh)!.add(area);
      }
    });
    return areas;
  }, [filters.chambers]);

  const weeklyFiltered = useMemo(() => {
    // Weekly entries (manual capacity) have NO pallet-type dimension.
    // When pallet-type filter is active, exclude manual capacity entirely —
    // otherwise it inflates totals for the selected pallet types.
    if (filters.palletTypes.length) return [];

    let out = weeklyEntries;
    if (impliedWarehouses)
      out = out.filter((e: any) => impliedWarehouses.has(String(e.warehouse || e.WAREHOUSE || '').trim()));
    // When chamber filter active, also scope by area
    if (impliedAreas) {
      out = out.filter((e: any) => {
        const wh = String(e.warehouse || e.WAREHOUSE || '').trim();
        const area = String(e.warehouse_area || '').trim();
        if (!area) return false; // no area info → exclude
        const whAreas = impliedAreas.get(wh);
        if (!whAreas) return false;
        return whAreas.has(area);
      });
    }
    return out;
  }, [weeklyEntries, impliedWarehouses, impliedAreas, filters.palletTypes]);

  // Filter contracts by selected warehouse(s) — or implied warehouses from
  // chamber selection. A contract matches when any of its warehouses is in
  // the active set, or it's an "All" contract.
  const contractsFiltered = useMemo(() => {
    if (!impliedWarehouses) return contracts;
    return (contracts || []).filter((c: any) => {
      const raw = String(c.facility ?? c.warehouse ?? c.WAREHOUSE ?? 'All').trim() || 'All';
      if (raw === 'All') return true;
      const whs = raw.split(',').map(s => s.trim()).filter(Boolean);
      return whs.some(w => impliedWarehouses.has(w));
    });
  }, [contracts, impliedWarehouses]);

  const whStats = useMemo(
    () => buildWhStats(rawAllFiltered, virtualFiltered, customerFiltered, areaBlockedFiltered, weeklyFiltered),
    [rawAllFiltered, virtualFiltered, customerFiltered, areaBlockedFiltered, weeklyFiltered],
  );

  // When pallet-type filter is active, we also need the BASE stats (same
  // warehouse+area scope but ALL pallet types) so we can compute each pallet
  // type's proportional share of non-pallet-attributable items.
  const rawAllNoPalletFilter = useMemo(
    () => filterRaw(rawAll, { warehouses: filters.warehouses, chambers: filters.chambers, palletTypes: [] }),
    [rawAll, filters.warehouses, filters.chambers],
  );
  const virtualNoPalletFilter = useMemo(
    () => filterRaw(virtual, { warehouses: filters.warehouses, chambers: filters.chambers, palletTypes: [] }),
    [virtual, filters.warehouses, filters.chambers],
  );
  const customerNoPalletFilter = useMemo(
    () => filterRaw(customerStorage, { warehouses: filters.warehouses, chambers: filters.chambers, palletTypes: [] }),
    [customerStorage, filters.warehouses, filters.chambers],
  );
  // weeklyFiltered already returns [] for pallet filter, so compute the
  // "full" weekly entries = warehouse+area scoped, no pallet exclusion
  const weeklyNoPalletFilter = useMemo(() => {
    let out = weeklyEntries;
    if (impliedWarehouses)
      out = out.filter((e: any) => impliedWarehouses.has(String(e.warehouse || e.WAREHOUSE || '').trim()));
    if (impliedAreas) {
      out = out.filter((e: any) => {
        const wh = String(e.warehouse || e.WAREHOUSE || '').trim();
        const area = String(e.warehouse_area || '').trim();
        if (!area) return false;
        const whAreas = impliedAreas.get(wh);
        return whAreas ? whAreas.has(area) : false;
      });
    }
    return out;
  }, [weeklyEntries, impliedWarehouses, impliedAreas]);

  // Sub-filter flags — control which warehouse-level items to include:
  //
  //  Data source        | Has area dim? | Has pallet dim? | Include when...
  //  -------------------|---------------|-----------------|------------------
  //  areaBlocked        | YES           | NO              | area OK, pallet NO
  //  commitment/shortfall| NO           | NO              | neither
  //  manualCap (weekly) | YES           | NO              | area OK, pallet NO (already handled by weeklyFiltered=[])
  //  customer (manual)  | YES           | NO              | area OK, pallet NO (already handled by filterRaw)
  //
  const hasPalletFilter = filters.palletTypes.length > 0;
  const hasSubFilters = filters.chambers.length > 0 || hasPalletFilter;

  const whStatsBase = useMemo(
    () => hasPalletFilter
      ? buildWhStats(rawAllNoPalletFilter, virtualNoPalletFilter, customerNoPalletFilter, areaBlockedFiltered, weeklyNoPalletFilter)
      : whStats,  // no pallet filter → base = filtered (same thing)
    [hasPalletFilter, rawAllNoPalletFilter, virtualNoPalletFilter, customerNoPalletFilter, areaBlockedFiltered, weeklyNoPalletFilter, whStats],
  );

  const kpis = useMemo(
    () => aggregate(
      filteredRows, rawAllFiltered, virtualFiltered,
      customerFiltered, areaBlockedFiltered, areaBlockedStats, contractsFiltered, whStats, customerFilteredWhOnly, hasSubFilters, hasPalletFilter, whStatsBase,
    ),
    [filteredRows, rawAllFiltered, virtualFiltered, customerFiltered, areaBlockedFiltered, areaBlockedStats, contractsFiltered, whStats, customerFilteredWhOnly, hasSubFilters, hasPalletFilter, whStatsBase],
  );

  /* Per-warehouse breakdown — passed to DrillModal so the level-0 drill
   * (Warehouse list) can show bin / virtual / floor / manual / blocked / commit
   * columns, mirroring the legacy ExecutiveDashboard. */
  const enrichedContracts = useMemo(
    () => enrichContracts(contractsFiltered, customerFilteredWhOnly),
    [contractsFiltered, customerFiltered],
  );

  // Distribute commitment SHORTFALL to specific warehouses (mirrors legacy
  // ExecutiveDashboard distribution: per-wh contracts → that wh; "All"
  // contracts → spread evenly across all known warehouses).
  const commitShortfallByWh = useMemo(() => {
    const map: Record<string, number> = {};
    const allWh = Object.keys(whStats);
    enrichedContracts.forEach(c => {
      if (c.shortfall <= 0) return;
      let targets: string[];
      if (c.warehouses.includes('All')) {
        targets = allWh;
      } else {
        // Only distribute to warehouses that actually exist in whStats.
        // Any share meant for non-existent warehouse names gets redistributed
        // to valid ones, keeping the total matching the KPI exactly.
        targets = c.warehouses.filter(w => allWh.includes(w));
        if (targets.length === 0) targets = allWh;  // fallback
      }
      if (targets.length === 0) return;
      // Distribute full shortfall across valid targets only
      const per = Math.ceil(c.shortfall / targets.length);
      let remaining = c.shortfall;
      targets.forEach(wh => {
        const share = Math.min(per, remaining);
        map[wh] = (map[wh] || 0) + share;
        remaining -= share;
      });
    });
    return map;
  }, [enrichedContracts, whStats]);

  const perWarehouse = useMemo(() => {
    const out: Record<string, any> = {};
    Object.values(whStats).forEach(w => {
      const wh = w.warehouse;
      const areaBFull = areaBlockedFiltered.filter(b => b.WAREHOUSE === wh)
        .reduce((s, b) => s + num(b.PALLET_COUNT ?? b.BLOCKED_PALLETS ?? b.TOTAL_NO_OF_SEGMENTS ?? 1), 0);
      const binCount = Object.values(w.rackByArea).reduce((s, a) => s + a.binCount, 0);

      let areaB: number, commit: number, manCap: number, manPal: number, floorP: number, virtU: number, blkCap: number;
      if (hasPalletFilter) {
        const base = whStatsBase[wh];
        const baseRackCap = base ? base.rackCap : 0;
        const ratio = baseRackCap > 0 ? w.rackCap / baseRackCap : 0;
        areaB   = Math.round(areaBFull * ratio);
        commit  = Math.round((commitShortfallByWh[wh] || 0) * ratio);
        manCap  = Math.round((base ? base.manualCap : 0) * ratio);
        manPal  = Math.round((base ? base.manualPallets : 0) * ratio);
        floorP  = Math.round((base ? base.floorPallets : 0) * ratio);
        virtU   = Math.round((base ? base.virtualUsed : 0) * ratio);
        blkCap  = w.blockedCap;  // blocked bins HAVE SHELF_SIZE → use actual filtered value
      } else {
        areaB  = areaBFull;
        commit = hasSubFilters ? 0 : (commitShortfallByWh[wh] || 0);
        manCap = w.manualCap; manPal = w.manualPallets;
        floorP = w.floorPallets; virtU = w.virtualUsed; blkCap = w.blockedCap;
      }
      const whTotalCap = w.rackCap + manCap;  // areaBlocked already in rackCap/blockedCap
      const whTotalUsed = w.rackUsed + blkCap + virtU + manPal + floorP + areaB + commit;
      out[wh] = {
        binCount, binUsed: w.rackUsed, binCap: w.rackCap,
        manualCap: manCap, bin: w.rackUsed, virtual: virtU,
        floor: floorP, manual: manPal,
        blocked: blkCap, tempBlocked: blkCap,
        areaBlocked: areaB, commit,
        totalCap: whTotalCap, totalUsed: whTotalUsed,
        free: Math.max(0, whTotalCap - whTotalUsed),
        rackByArea: w.rackByArea, manualByLocation: w.manualByLocation,
        areaBlockedByArea: areaBlockedFiltered
          .filter(b => b.WAREHOUSE === wh)
          .map(b => ({
            ...b,
            AREA: String(b.STORAGE_LOCATION ?? b.STORAGE_AREA ?? '').trim(),
            COUNT: num(b.PALLET_COUNT ?? b.BLOCKED_PALLETS ?? b.TOTAL_NO_OF_SEGMENTS ?? 1),
          })),
      };
    });
    return out;
  }, [whStats, whStatsBase, areaBlockedFiltered, commitShortfallByWh, hasPalletFilter, hasSubFilters]);

  const P = theme.palette;

  /* ─────────────────────────────────────────────────────────────────
   * Warehouse breakdown — one row per warehouse, driven by the
   * already-filtered `whStats`. Columns:
   *   • totalBin    — physical rack capacity (bin segments)
   *   • totalCap    — rackCap + blockedCap + manualCap
   *   • usedCap     — tUsed (rack + blocked + virtual + manual + floor)
   *   • sellable    — tFree − committed (free to sell right now)
   *   • committed   — minimum-commit pallets from customer contracts
   *   • util        — used / totalCap × 100
   * Reacts to the FilterBar automatically because whStats is built
   * from the filtered raw arrays.
   * ────────────────────────────────────────────────────────────────*/
  const warehouseData = useMemo(() => {
    const commitByWh = commitShortfallByWh;
    return Object.values(whStats)
      .map(w => {
        const wh = w.warehouse;

        // --- Area-blocked (has area dim, NO pallet dim) ---
        const areaBFull = areaBlockedFiltered
          .filter(b => b.WAREHOUSE === wh)
          .reduce((s, b) => s + num(b.PALLET_COUNT ?? b.BLOCKED_PALLETS ?? b.TOTAL_NO_OF_SEGMENTS ?? 1), 0);

        // --- Commitment shortfall (no area, no pallet dim) ---
        const commitFull = commitByWh[wh] || 0;

        if (!hasPalletFilter) {
          // No pallet filter → area-blocked included as-is, commitment only at warehouse level
          const areaB = areaBFull;
          const committed = hasSubFilters ? 0 : commitFull;
          const totalCap = w.tCap;  // areaBlocked already in rackCap/blockedCap
          const usedCap = w.tUsed + areaB + committed;
          const sellable = Math.max(0, totalCap - usedCap);
          const binCount = Object.values(w.rackByArea).reduce((s, a) => s + a.binCount, 0);
          return {
            warehouse: wh, warehouseLabel: aliasResolve('warehouse', wh),
            totalBin: binCount, totalCap, usedCap, sellable, committed,
            util: totalCap > 0 ? Math.min(100, Math.round((usedCap / totalCap) * 100)) : 0,
            overcommit: usedCap > totalCap,
          };
        }

        // --- Pallet filter active: proportional distribution ---
        // whStats (filtered) has only selected pallet types' rack data.
        // whStatsBase has ALL pallet types (same wh+area scope).
        // Ratio = filtered rackCap / base rackCap → share of non-pallet items.
        //
        // Items WITH pallet dim (SHELF_SIZE): use actual filtered values
        //   → rackCap, rackUsed, blockedCap (blocked bins are physical bins with SHELF_SIZE)
        // Items WITHOUT pallet dim: distribute proportionally
        //   → manualCap, manualPallets, floorPallets, virtualUsed, areaBlocked, commitment
        const base = whStatsBase[wh];
        const baseRackCap = base ? base.rackCap : 0;
        const ratio = baseRackCap > 0 ? w.rackCap / baseRackCap : 0;

        // Non-pallet items → proportional share
        const manualCapShare   = Math.round((base ? base.manualCap : 0) * ratio);
        const manualPalShare   = Math.round((base ? base.manualPallets : 0) * ratio);
        const floorShare       = Math.round((base ? base.floorPallets : 0) * ratio);
        const virtualShare     = Math.round((base ? base.virtualUsed : 0) * ratio);
        const areaBShare       = Math.round(areaBFull * ratio);
        const commitShare      = Math.round(commitFull * ratio);
        // blockedCap → actual filtered value (blocked bins HAVE SHELF_SIZE)

        const totalCap = w.rackCap + manualCapShare;  // areaBlocked already in rackCap/blockedCap
        const usedCap  = w.rackUsed + w.blockedCap + virtualShare + manualPalShare + floorShare + areaBShare + commitShare;
        const sellable = Math.max(0, totalCap - usedCap);
        const binCount = Object.values(w.rackByArea).reduce((s, a) => s + a.binCount, 0);
        return {
          warehouse: wh, warehouseLabel: aliasResolve('warehouse', wh),
          totalBin: binCount, totalCap, usedCap, sellable, committed: commitShare,
          util: totalCap > 0 ? Math.min(100, Math.round((usedCap / totalCap) * 100)) : 0,
          overcommit: usedCap > totalCap,
        };
      })
      .filter(r => r.totalCap > 0 || r.usedCap > 0)
      .sort((a, b) => b.totalCap - a.totalCap);
  }, [whStats, whStatsBase, commitShortfallByWh, areaBlockedFiltered, aliasResolve, hasSubFilters, hasPalletFilter]);

  /* ─── Pallet Type breakdown ───────────────────────────────────────
   * One row per SHELF_SIZE across the filtered rows. Shows capacity,
   * used, and sellable per pallet type — mirrors the warehouse chart.
   * ────────────────────────────────────────────────────────────────*/
  const palletTypeData = useMemo(() => {
    const byType: Record<string, { cap: number; used: number; bins: number }> = {};
    filteredRows.forEach(r => {
      const t = String(r.SHELF_SIZE ?? '').trim();
      if (!t) return;
      const b = (byType[t] ||= { cap: 0, used: 0, bins: 0 });
      b.cap  += num(r.TOTAL_BINS);
      b.used += num(r.USED_BINS);
      b.bins += 1;
    });
    return Object.entries(byType)
      .map(([t, v]) => {
        const sellable = Math.max(0, v.cap - v.used);
        return {
          palletType: t,
          palletLabel: aliasResolve('palletType', t),
          totalBin: v.bins,
          totalCap: v.cap,
          usedCap: v.used,
          sellable,
          util: v.cap > 0 ? Math.min(100, Math.round((v.used / v.cap) * 100)) : 0,
        };
      })
      .filter(r => r.totalCap > 0 || r.usedCap > 0)
      .sort((a, b) => b.totalCap - a.totalCap);
  }, [filteredRows, aliasResolve]);

  // Derive Overall Utilization from warehouseData so chart + donut always match
  const whTotalCap = useMemo(() => warehouseData.reduce((s, w) => s + w.totalCap, 0), [warehouseData]);
  const whTotalUsed = useMemo(() => warehouseData.reduce((s, w) => s + w.usedCap, 0), [warehouseData]);
  const whTotalSellable = useMemo(() => warehouseData.reduce((s, w) => s + w.sellable, 0), [warehouseData]);

  const donutData = useMemo(() => {
    const occ = kpis.breakdown.bin + kpis.breakdown.floor + kpis.breakdown.manual + kpis.breakdown.virtual;
    const tempBlocked = kpis.breakdown.tempBlocked;
    const areaBlocked = kpis.breakdown.areaBlocked;
    const committed = kpis.shortfallCapacity;
    const available = Math.max(0, whTotalCap - occ - tempBlocked - areaBlocked - committed);
    const total = Math.max(1, occ + tempBlocked + areaBlocked + committed + available);
    return [
      { name: 'Occupied',      value: occ,          color: P.occupied },
      { name: 'Available',     value: available,     color: P.sellable },
      { name: 'Committed',     value: committed,     color: P.committed },
      { name: 'Temp Blocked',  value: tempBlocked,   color: '#ef4444' },
      { name: 'Area Blocked',  value: areaBlocked,   color: '#be123c' },
    ].filter(s => s.value > 0).map(s => ({ ...s, pct: Math.round((s.value / total) * 100) }));
  }, [kpis, P, whTotalCap]);

  const utilPct = useMemo(() => {
    if (!whTotalCap) return 0;
    return Math.round((whTotalUsed / whTotalCap) * 100);
  }, [whTotalCap, whTotalUsed]);

  // Semantic colours — match the mockup legend exactly:
  // Occupied=blue, Committed=amber, Future=grey(dashed), Blocked=red, Sellable=green.
  const totalBins = useMemo(
    () => Object.values(perWarehouse).reduce((s: number, b: any) => s + (b.binCount || 0), 0),
    [perWarehouse],
  );

  // Use warehouseData totals for Cap/Used/Sellable so cards, header, and chart
  // are always in sync. kpis.committed / kpis.shortfall are still from aggregate
  // because commitment logic is warehouse-level and already included in warehouseData.
  const cards = useMemo(() => ([
    { key: 'totalBins',         title: 'Total Location',       subtitle: 'Physical rack locations',   value: totalBins,                accent: P.neutral,   unit: 'Locations' },
    { key: 'totalCapacity',     title: 'Total Capacity',      subtitle: 'Bin + Manual',              value: whTotalCap,               accent: P.neutral,   unit: 'Pallets' },
    { key: 'usedCapacity',      title: 'Occupied Capacity',   subtitle: 'Physical + Floor + Manual', value: whTotalUsed,              accent: P.occupied,  unit: 'Pallets' },
    { key: 'sellableCapacity',  title: 'Sellable Capacity',   subtitle: 'Free to sell now',          value: whTotalSellable,          accent: P.sellable,  unit: 'Pallets' },
    { key: 'committedCapacity', title: 'Committed Capacity',  subtitle: 'Min. commitment per customer', value: kpis.committedCapacity, accent: P.committed, unit: 'Pallets' },
    { key: 'shortfallCapacity', title: 'Shortfall Capacity',  subtitle: 'Unfilled commitment gap',   value: kpis.shortfallCapacity,   accent: '#dc2626',   unit: 'Pallets' },
  ]), [kpis, P, totalBins, whTotalCap, whTotalUsed, whTotalSellable]);

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.pageBg,
      fontFamily: theme.fontFamily,
      color: theme.textPrimary,
      display: 'flex', flexDirection: 'column',
    }}>
      <AppBar
        title={activeTab === 'production' ? 'Operations Console' : 'Commercial Console'}
        subtitle={activeTab === 'production' ? 'Capacity · Restrictions · Commitments' : 'Commercial Intelligence · Capacity · Commitments'}
        loading={loading}
        error={error}
        rowCount={rows.length}
        paused={paused}
        onTogglePause={() => setPaused(p => !p)}
        onRefresh={refresh}
        lastRefresh={lastRefresh}
      />

      {/* ╭─── TAB SWITCHER ───╮ */}
      <div style={{
        maxWidth: 1840, width: '100%', margin: '0 auto',
        padding: '12px 32px 0',
        boxSizing: 'border-box',
      }}>
        <div style={{
          display: 'inline-flex', gap: 4,
          background: '#f1f5f9', borderRadius: 12, padding: 4,
        }}>
          {([
            { key: 'production' as DashTab, label: 'Operations Console', icon: '' },
            { key: 'sales' as DashTab, label: 'Commercial Console', icon: '' },
          ]).map(tab => {
            const active = activeTab === tab.key;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                padding: '10px 24px', borderRadius: 10, border: 'none',
                background: active
                  ? 'linear-gradient(135deg, #1e3a8a, #3b82f6)'
                  : 'transparent',
                color: active ? '#fff' : theme.textSecondary,
                fontSize: 13, fontWeight: 700, cursor: 'pointer',
                fontFamily: theme.fontFamily, letterSpacing: -0.1,
                transition: 'all 0.2s ease',
                display: 'flex', alignItems: 'center', gap: 8,
                boxShadow: active ? '0 2px 8px rgba(30,58,138,0.25)' : 'none',
              }}>
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{
        flex: 1,
        maxWidth: 1840, width: '100%', margin: '0 auto',
        padding: '14px 32px 36px',
        display: 'flex', flexDirection: 'column', gap: 20,
        boxSizing: 'border-box',
      }}>

      {/* ╭─── SALES TAB ───╮ */}
      {activeTab === 'sales' && (
        <SalesDashboard
          kpis={kpis}
          totalBins={totalBins}
          warehouseData={warehouseData}
          enrichedContracts={enrichedContracts}
          customerStorage={customerFiltered}
          weeklyEntries={weeklyEntries}
          rows={filteredRows}
          loading={loading}
        />
      )}

      {/* ╭─── PRODUCTION TAB ───╮ */}
      {activeTab === 'production' && (<>
        {/* Shared SVG gradient defs used by every chart */}
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <linearGradient id="gradOccupied" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={P.occupied} stopOpacity={1} />
              <stop offset="100%" stopColor={P.occupied} stopOpacity={0.55} />
            </linearGradient>
            <linearGradient id="gradSellable" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={P.sellable} stopOpacity={1} />
              <stop offset="100%" stopColor={P.sellable} stopOpacity={0.55} />
            </linearGradient>
            <linearGradient id="gradCommitted" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={P.committed} stopOpacity={1} />
              <stop offset="100%" stopColor={P.committed} stopOpacity={0.55} />
            </linearGradient>
            <linearGradient id="gradNeutral" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={P.neutral} stopOpacity={1} />
              <stop offset="100%" stopColor={P.neutral} stopOpacity={0.55} />
            </linearGradient>
            <linearGradient id="gradIndigo" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor="#6366f1" stopOpacity={1} />
              <stop offset="100%" stopColor="#6366f1" stopOpacity={0.55} />
            </linearGradient>
          </defs>
        </svg>

        {/* ╭─── HERO HEADER: gradient band with overall utilization & quick stats ───╮ */}
        <div style={{
          position: 'relative',
          background: `linear-gradient(135deg, #1e3a8a 0%, #3b82f6 55%, #0891b2 100%)`,
          borderRadius: 18,
          padding: '22px 28px',
          color: '#fff',
          boxShadow: '0 10px 30px rgba(30,58,138,0.18), 0 2px 6px rgba(30,58,138,0.1)',
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(600px 220px at 90% -20%, rgba(255,255,255,0.18), transparent 70%)',
            pointerEvents: 'none',
          }} />
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.4, opacity: 0.75, fontWeight: 600 }}>Operations · Live Overview</div>
              <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, letterSpacing: -0.3 }}>Warehouse Capacity Intelligence</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 3 }}>
                {fmtNum(whTotalCap)} total capacity ·&nbsp;
                <span style={{ fontWeight: 600, color: '#d1fae5' }}>{fmtNum(whTotalSellable)} sellable capacity</span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
              <HeroStat label="Occupied" value={fmtNum(kpis.usedCapacity)} tint="#bfdbfe" />
              <HeroStat label="Available" value={fmtNum(kpis.freeCapacity)} tint="#bbf7d0" />
              <HeroStat label="Committed" value={fmtNum(kpis.committedCapacity)} tint="#fde68a" />
              <HeroStat label="Shortfall" value={fmtNum(kpis.shortfallCapacity)} tint="#fecaca" />
              <div style={{
                width: 92, height: 92, borderRadius: '50%',
                background: 'rgba(255,255,255,0.14)',
                border: '2px solid rgba(255,255,255,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column',
                boxShadow: 'inset 0 0 24px rgba(255,255,255,0.15)',
              }}>
                <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1, letterSpacing: -0.5 }}>{utilPct}%</div>
                <div style={{ fontSize: 9, opacity: 0.85, marginTop: 3, textTransform: 'uppercase', letterSpacing: 1 }}>Utilized</div>
              </div>
            </div>
          </div>
        </div>

        {/* ╭─── FILTER STRIP ───╮ */}
        <div style={{
          background: '#ffffff',
          border: '1px solid #e5eaf2',
          borderRadius: 14,
          padding: '14px 22px',
          boxShadow: '0 1px 2px rgba(20,40,80,0.04)',
        }}>
          <FilterBar rows={rows} customerStorage={customerStorage} value={filters} onChange={setFilters} />
        </div>

        {/* ╭─── KPI CARDS (6) ───╮ */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
          gap: 14,
        }}>
          {cards.map(c => (
            <KpiCard key={c.key}
              title={c.title} subtitle={c.subtitle}
              value={c.value} unit={(c as any).unit} loading={loading}
              accent={c.accent}
              onClick={() => setDrill({ key: c.key, title: c.title, accent: c.accent })}
            />
          ))}
        </div>

        {/* ╭─── Charts row: Warehouse Util + Overall Donut ───╮ */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: 18,
        }}>
          <ChartPanel title="Warehouse Capacity Utilization" subtitle="Used & Sellable pallets per warehouse with live utilization trendline">
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={warehouseData} margin={{ top: 16, right: 24, left: 0, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 4" stroke="#eef2f8" vertical={false} />
                <XAxis dataKey="warehouseLabel" tick={{ fill: theme.textSecondary, fontSize: 12, fontWeight: 600 }} interval={0} tickLine={false} axisLine={{ stroke: '#e5eaf2' }} />
                <YAxis yAxisId="left" tick={{ fill: theme.textMuted, fontSize: 11 }} tickFormatter={(v) => fmtNum(v)} tickLine={false} axisLine={false} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fill: theme.textMuted, fontSize: 11 }} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={false} />
                <RTooltip content={<PrettyTooltip />} cursor={{ fill: 'rgba(99,102,241,0.05)' }} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" />
                <Bar yAxisId="left" dataKey="usedCap"  name="Used"     stackId="a" fill="url(#gradOccupied)" radius={[0, 0, 0, 0]} />
                <Bar yAxisId="left" dataKey="sellable" name="Sellable" stackId="a" fill="url(#gradSellable)" radius={[6, 6, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="util" name="Util %" stroke={P.committed} strokeWidth={3} dot={{ r: 4, fill: '#fff', stroke: P.committed, strokeWidth: 2 }} activeDot={{ r: 6 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartPanel>

          <ChartPanel title="Total Capacity" subtitle={`${fmtNum(whTotalUsed)} of ${fmtNum(whTotalCap)} pallets`}>
            <div style={{ position: 'relative' }}>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={donutData}
                    dataKey="value" nameKey="name"
                    innerRadius={74} outerRadius={108}
                    paddingAngle={3} stroke="#fff" strokeWidth={3}
                    startAngle={90} endAngle={-270}
                  >
                    {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <RTooltip content={<PrettyTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              {/* Center label overlay */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'none',
              }}>
                <div style={{ fontSize: 34, fontWeight: 800, color: theme.textPrimary, letterSpacing: -1, lineHeight: 1 }}>{utilPct}%</div>
                <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 1.2, marginTop: 4, fontWeight: 600 }}>Utilized</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
              {donutData.map(d => (
                <div key={d.name} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 10px',
                  background: '#f8fafc',
                  border: '1px solid #eef2f8',
                  borderRadius: 8,
                }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: d.color, boxShadow: `0 0 0 3px ${d.color}22`, flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>{d.name}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: theme.textPrimary }}>{d.pct}%</div>
                  </div>
                </div>
              ))}
            </div>
          </ChartPanel>
        </div>

        {/* ╭─── Charts row 2: Capacity by Warehouse (2fr) + Capacity by Pallet Type (1fr) ───╮ */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: 18,
        }}>
          <ChartPanel title="Capacity Breakdown by Warehouse" subtitle="Total Locations · Total Capacity · Used · Sellable per warehouse">
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={warehouseData} margin={{ top: 16, right: 24, left: 0, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 4" stroke="#eef2f8" vertical={false} />
                <XAxis dataKey="warehouseLabel" tick={{ fill: theme.textSecondary, fontSize: 12, fontWeight: 600 }} interval={0} tickLine={false} axisLine={{ stroke: '#e5eaf2' }} />
                <YAxis tick={{ fill: theme.textMuted, fontSize: 11 }} tickFormatter={(v) => fmtNum(v)} tickLine={false} axisLine={false} />
                <RTooltip content={<PrettyTooltip />} cursor={{ fill: 'rgba(99,102,241,0.05)' }} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" />
                <Bar dataKey="totalBin"  name="Total Locations" fill="url(#gradIndigo)"    radius={[6, 6, 0, 0]} />
                <Bar dataKey="totalCap"  name="Total Capacity" fill="url(#gradNeutral)"   radius={[6, 6, 0, 0]} />
                <Bar dataKey="usedCap"   name="Used Capacity"  fill="url(#gradOccupied)"  radius={[6, 6, 0, 0]} />
                <Bar dataKey="sellable"  name="Sellable"       fill="url(#gradSellable)"  radius={[6, 6, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartPanel>

          <ChartPanel title="Capacity by Pallet Type" subtitle="Used vs Sellable with utilization %">
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={palletTypeData} margin={{ top: 16, right: 24, left: 0, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 4" stroke="#eef2f8" vertical={false} />
                <XAxis dataKey="palletLabel" tick={{ fill: theme.textSecondary, fontSize: 12, fontWeight: 600 }} interval={0} tickLine={false} axisLine={{ stroke: '#e5eaf2' }} />
                <YAxis yAxisId="left" tick={{ fill: theme.textMuted, fontSize: 11 }} tickFormatter={(v) => fmtNum(v)} tickLine={false} axisLine={false} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fill: theme.textMuted, fontSize: 11 }} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={false} />
                <RTooltip content={<PrettyTooltip />} cursor={{ fill: 'rgba(99,102,241,0.05)' }} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" />
                <Bar yAxisId="left" dataKey="usedCap"  name="Used"     stackId="a" fill="url(#gradOccupied)" radius={[0, 0, 0, 0]} />
                <Bar yAxisId="left" dataKey="sellable" name="Sellable" stackId="a" fill="url(#gradSellable)" radius={[6, 6, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="util" name="Util %" stroke={P.committed} strokeWidth={3} dot={{ r: 4, fill: '#fff', stroke: P.committed, strokeWidth: 2 }} activeDot={{ r: 6 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartPanel>
        </div>

        {drill && (
          <DrillModal
            kpiKey={drill.key}
            kpiTitle={drill.title}
            accent={drill.accent}
            rows={filteredRows}
            perWarehouse={perWarehouse}
            customerStorage={customerFiltered}
            commitments={enrichedContracts}
            onClose={() => setDrill(null)}
          />
        )}
      </>)}
      </div>
    </div>
  );
};

const SectionLabel: React.FC<{ text: string }> = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '8px 0 16px' }}>
    <div style={{ flex: 1, height: 1, background: '#e5eaf2' }} />
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: 1.4,
      color: theme.textSecondary, textTransform: 'uppercase',
      padding: '0 4px',
    }}>
      {text}
    </div>
    <div style={{ flex: 1, height: 1, background: '#e5eaf2' }} />
  </div>
);

const ChartPanel: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => {
  const [maximized, setMaximized] = useState(false);

  const panel = (
    <div style={{
      background: '#ffffff',
      border: maximized ? 'none' : '1px solid #e5eaf2',
      borderRadius: maximized ? 0 : 16,
      padding: maximized ? '28px 36px 24px' : '20px 22px 14px',
      boxShadow: maximized ? 'none' : '0 2px 10px rgba(20,40,80,0.05), 0 1px 2px rgba(20,40,80,0.03)',
      height: maximized ? '100%' : 'auto',
      display: maximized ? 'flex' : 'block',
      flexDirection: 'column' as const,
      overflow: maximized ? 'auto' : 'visible',
    }}>
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: maximized ? 20 : 15, fontWeight: 700, color: theme.textPrimary, letterSpacing: -0.2 }}>{title}</div>
          {subtitle && <div style={{ fontSize: maximized ? 13 : 11.5, color: theme.textMuted, marginTop: 3 }}>{subtitle}</div>}
        </div>
        <button
          onClick={() => setMaximized(m => !m)}
          title={maximized ? 'Minimize' : 'Maximize'}
          style={{
            background: 'none', border: '1px solid #d1d9e6', borderRadius: 6,
            cursor: 'pointer', padding: '4px 6px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: theme.textMuted, fontSize: 14, lineHeight: 1,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#f0f4fa')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
        >
          {maximized ? '✕' : '⛶'}
        </button>
      </div>
      <div style={maximized ? { flex: 1, minHeight: 0 } : {}}>
        {children}
      </div>
    </div>
  );

  if (!maximized) return panel;

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 9999, background: '#ffffff',
      overflow: 'auto',
    }}>
      {panel}
    </div>,
    document.body,
  );
}

const HeroStat: React.FC<{ label: string; value: string; tint: string }> = ({ label, value, tint }) => (
  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.1, opacity: 0.8, fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: tint, letterSpacing: -0.4, lineHeight: 1.15, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
  </div>
);

const PrettyTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      background: '#ffffff',
      border: '1px solid #e5eaf2',
      borderRadius: 10,
      padding: '10px 14px',
      boxShadow: '0 8px 24px rgba(20,40,80,0.12)',
      fontSize: 12,
      minWidth: 160,
    }}>
      {label && (
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #eef2f8' }}>
          {label}
        </div>
      )}
      {payload.map((p: any, i: number) => {
        const isPct = p.name === 'Util %';
        const val = isPct ? `${p.value}%` : fmtNum(Number(p.value));
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, padding: '3px 0' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.textSecondary }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: p.color || p.payload?.color, display: 'inline-block' }} />
              {p.name}
            </span>
            <strong style={{ color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{val}</strong>
          </div>
        );
      })}
    </div>
  );
};

export default OperationsDashboard;
